// src/hooks/useAssets.js
// ============================================================================
// Shared asset data hooks.
//
// 1A-1: useSignedUrlMap extracted (signer).                        [committed]
// 1A-3: fetchAssets removed from Assets.js (single writer).        [committed]
// 1A-4: useAssets extracted (realtime listener + scope filter + sort).  <-- THIS STEP
//
// All logic in this file is transcribed VERBATIM from src/pages/Assets.js at
// the post-1A-3 committed baseline. The only intentional deviation from the
// original inline code is the stable-default-array fix in useSignedUrlMap
// (documented below). Because 1A-3 already reduced Assets.js to a SINGLE writer
// of `assets` state, the 1A-4 extraction moves exactly one writer — no
// refetch(), no competing setters, no behavior change.
//
// 1B-1: useProgramAssets added (Program Builder picker wrapper) — the first
//       deliberately Builder-specific piece. It applies the program predicate
//       to My-Files-scoped assets. NO asset->ProgramEntry transform here; that
//       is 1B-2, written from real runtime evidence (see the inspection harness).
//
// Layering (dependency direction: generic -> specialization):
//   useSignedUrlMap(assets, assetTypes) -> batched CloudFront signer
//   useAssets({ viewMode, scope })      -> realtime listener + own+shared/scope
//                                          filter + sort; signs video+image
//   useProgramAssets()                  -> My-Files scope + program predicate
//                                          (Program Builder picker; 1B-1)
// ============================================================================

import { useState, useEffect } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import devLog from '../utils/devLog';

// Module-level constant so the reference is STABLE across renders.
// (A fresh `['video','image']` literal as an effect dependency would look
// "changed" every render and re-run the signer unnecessarily.)
const DEFAULT_SIGN_TYPES = ['video', 'image'];

// ---------------------------------------------------------------------------
// useSignedUrlMap — verbatim signer (Assets.js signer effect), type-parameterized.
// Returns { [key]: signedUrl }. Default types match Assets(): video + image.
// (Unchanged from 1A-1.)
// ---------------------------------------------------------------------------
export function useSignedUrlMap(assets, assetTypes = DEFAULT_SIGN_TYPES) {
  const [signedUrlMap, setSignedUrlMap] = useState({});

  useEffect(() => {
    const fetchSignedUrls = async () => {
      const eligible = assets.filter(a => assetTypes.includes(a.type));
      if (eligible.length === 0) return;

      const keys = eligible.map(a =>
        a.key || a.url?.replace('https://pulse-signage.s3.us-east-2.amazonaws.com/', '')
      ).filter(Boolean);

      if (keys.length === 0) return;

      try {
        const functions = getFunctions();
        const getSignedUrls = httpsCallable(functions, 'getSignedUrls');

        const chunks = [];
        for (let i = 0; i < keys.length; i += 50) {
          chunks.push(keys.slice(i, i + 50));
        }

        const allSignedUrls = {};
        for (const chunk of chunks) {
          const result = await getSignedUrls({ keys: chunk });
          if (result.data?.signedUrls) {
            Object.assign(allSignedUrls, result.data.signedUrls);
          }
        }

        setSignedUrlMap(allSignedUrls);
      } catch (error) {
        console.error('Failed to fetch signed URLs:', error);
      }
    };

    fetchSignedUrls();
  }, [assets, assetTypes]);

  return signedUrlMap;
}

// ---------------------------------------------------------------------------
// useAssets — realtime listener + EXACT own+shared/scope filter + sort,
// transcribed VERBATIM from the post-1A-3 baseline (Assets.js lines 160-203).
// Single writer to `assets` (1A-3 removed the other). Signs video+image.
// Returns { assets, signedUrlMap, loading }.
// ---------------------------------------------------------------------------
export function useAssets({ viewMode, scope }) {
  const { currentUser } = useAuth();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'assets'),
      (snapshot) => {
        devLog('Firestore update received:', snapshot.docs.length, 'assets');
        const assetsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })).filter(asset => {
          // Admin view:
          if (viewMode === 'admin') {
            // Layouts: own + shared templates, never other users' provisioned layouts (unchanged).
            if (asset.type === 'layout') {
              if (asset.userId && asset.userId !== currentUser?.uid) return false;
              return true;
            }
            // Non-layout (videos/images): only admin's own + the shared library, never other users' content.
            return !asset.userId || asset.userId === currentUser?.uid;
          }

          // User view: filter by scope toggle (Library vs My Files)
          if (scope === 'library') {
            // Library = shared content with no userId stamped on it.
            // Layouts are never shared — users only see their provisioned copies in My Files.
            if (asset.type === 'layout') return false;
            return !asset.userId;
          }

          // My Files = tenant-private content belonging to current user.
          return asset.userId === currentUser?.uid;
        });
        const getTs = (t) => !t ? 0 : typeof t === 'number' ? t : t.toMillis ? t.toMillis() : t.seconds ? t.seconds * 1000 : 0;
        assetsData.sort((a, b) => getTs(b.uploadedAt) - getTs(a.uploadedAt));
        setAssets(assetsData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching assets:', error);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [viewMode, scope, currentUser?.uid]);

  const signedUrlMap = useSignedUrlMap(assets, DEFAULT_SIGN_TYPES);

  return { assets, signedUrlMap, loading };
}

// ---------------------------------------------------------------------------
// useProgramAssets — Program Builder picker wrapper (1B-1).
// My-Files scope (⇒ userId === uid, which OWNED program clips need) + the
// program predicate. Program clips are OWNED (Exercise Builder stamps userId),
// so My-Files scope is correct for them. Returns the raw asset objects — NO
// ProgramEntry transform here (that is 1B-2, written from real evidence).
// ---------------------------------------------------------------------------
export function useProgramAssets() {
  const { assets, signedUrlMap, loading } = useAssets({ viewMode: 'user', scope: 'my-files' });
  const programClips = assets.filter(
    (asset) => asset.source === 'exercise-builder' && asset.pageMode === 'program'
  );
  return { programClips, signedUrlMap, loading };
}